import { useEffect, useState } from 'react';
import { RoleCard } from '../components/RoleCard.js';
import { RoleRevealAura } from '../components/RoleRevealAura.js';
import { Smoke, Dust } from '../components/Atmosphere.js';
import { RoleRevealInfo } from '../components/RoleRevealInfo.js';
import { usePerfLite } from '../usePerfLite.js';
import type { ClientMsg, Lang, PlayerView } from '../../../shared/src/types.js';

interface Props {
  t: Lang;
  view: PlayerView;
  send: (msg: ClientMsg) => void;
}

// The role reveal is the emotional centerpiece. Sequence:
//  1. tap-cover ("HOLD PHONE TO YOUR FACE") — prevents accidental reveal
//  2. card flies in face-down (stage 1)
//  3. card flips, ARSONIST-style stamp lands (stage 2)
//  4. tap "memorized" — re-cover and ack to server.
export function RoleReveal({ t, view, send }: Props) {
  const role = view.me.role;
  // NOTE: the defensive "no role" bail-out lives BELOW the hook calls —
  // returning before them violated the Rules of Hooks (audit P2): if a
  // broadcast ever flipped `role` between defined/undefined across renders,
  // React's hook bookkeeping would corrupt and crash the whole tree.
  const lite = usePerfLite();
  const [covered, setCovered] = useState(true);
  // Set true once the player confirms — switches the cover from the
  // tap-to-reveal affordance to a passive "role memorized · waiting" state so
  // an already-acked player isn't invited to re-reveal while the rest finish.
  const [acked, setAcked] = useState(false);
  // Reveal sequence stages (rendered in this order after `covered` flips off):
  //   0 — pre-deal: nothing yet
  //   1 — face-down dealt in (deck back lands, charging aura builds up)
  //   2 — flipping: rotateY runs, light flash bursts, scale punch fires
  //   3 — landed face-up: entrance animation runs, ability info fades in
  const [stage, setStage] = useState(0);
  // `flashing` punctuates the flip moment (light burst, scale punch). It
  // fires while the card is mid-rotation.
  const [flashing, setFlashing] = useState(false);
  // `revealing` drives the per-archetype entrance animation on the FRONT
  // face. It MUST only fire after the flip has landed — otherwise the
  // entrance plays on the back side and shows through mirrored as the card
  // is still rotating through the 90°-180° half of the flip.
  const [revealing, setRevealing] = useState(false);

  // Known teammates: 'ally:<playerId>' marks resolved to current display names
  // (drop any that no longer resolve, e.g. a left player).
  const allyNames = view.me.marks
    .filter((m) => m.startsWith('ally:'))
    .map((m) => view.room.players.find((p) => p.id === m.slice(5))?.name)
    .filter((nm): nm is string => !!nm);

  useEffect(() => {
    if (covered) {
      setStage(0);
      setFlashing(false);
      setRevealing(false);
      return;
    }
    // Under perf-lite the charge pulse, flip burst and per-archetype entrance
    // keyframes are all suppressed by CSS, so the full cinematic ladder is just
    // ~2s of invisible dead air on a static deck back. Collapse it: a brief
    // stage-1 beat (so the card still deals in) then jump straight to stage 3
    // so the role + side/goal/ability info appear almost immediately.
    if (lite) {
      const ids = [
        setTimeout(() => setStage(1), 120),
        setTimeout(() => setStage(3), 260),
      ];
      return () => ids.forEach(clearTimeout);
    }
    // Timeline:
    //   0      → 350ms : deal-in (face-down arrives, aura wash appears)
    //   350    → 1300  : charging (medallion pulses, tension builds)
    //   1300          : flip starts (data-flipped=false) + light burst fires
    //   1300   → ~2060: rotateY transition runs (760ms)
    //   2060          : flip lands; entrance animation kicks in
    //   ~2860         : revealing=false → idle animations resume
    const ids = [
      setTimeout(() => setStage(1), 350),
      setTimeout(() => { setStage(2); setFlashing(true); }, 1300),
      setTimeout(() => { setStage(3); setFlashing(false); setRevealing(true); }, 2060),
      setTimeout(() => setRevealing(false), 2860),
    ];
    return () => ids.forEach(clearTimeout);
  }, [covered, lite]);

  // Defensive — App routes us here only during 'role-reveal' phase, when the
  // server has assigned a role. If we somehow got here without one, render
  // nothing rather than crashing. Placed AFTER every hook (Rules of Hooks).
  if (!role) return null;

  return (
    <div className="scr scr-reveal">
      <div className="bg-velvet" />
      <Smoke />
      <Dust count={14} />
      {!covered && <RoleRevealAura role={role} stage={stage} />}
      <div className="vig vignette-pulse" />

      <div className="reveal-head">
        <div className="sub">{t === 'ru' ? 'ВАША РОЛЬ' : 'ВАША РОЛЬ'}</div>
        <div className="mono reveal-meta">
          {t === 'ru' ? 'НИКТО ДРУГОЙ НЕ ВИДИТ' : 'НІХТО ІНШИЙ НЕ БАЧИТЬ'}
        </div>
      </div>

      <div className="reveal-stage" data-stage={stage}>
        {stage >= 1 && (
          <>
            <div className="reveal-final">
              <RoleCard role={role} t={t} flipped={stage < 2} flashing={flashing} revealing={revealing} />
            </div>

            {/* Role NAME + SIDE + WIN CONDITION + ability + team — kept inside
                the stage so they sit just below the card, instead of being
                pushed to the foot with a wide empty gap between. Shared with
                the card gallery via RoleRevealInfo so they can't drift. Only
                after the flip. */}
            {stage >= 3 && <RoleRevealInfo role={role} t={t} allies={allyNames} />}
          </>
        )}
      </div>

      <div className="reveal-foot">
        {stage >= 3 ? (
          <button
            className="btn btn-blood"
            style={{ flex: 1 }}
            onClick={() => {
              setAcked(true);
              setCovered(true);
              send({ kind: 'player:reveal-ack' });
            }}
          >
            <span className="display">{t === 'ru' ? 'ГОТОВО — СКРЫТЬ' : 'ГОТОВО — СХОВАТИ'}</span>
          </button>
        ) : (
          <div className="reveal-hint mono">{t === 'ru' ? 'РАЗДАЁМ КАРТУ…' : 'РОЗДАЄМО КАРТУ…'}</div>
        )}
      </div>

      {covered && acked && (
        // Already memorized — passive waiting cover, NO tap-to-reveal. The
        // server unmounts this screen once everyone acks; this just fills the
        // gap so an acked player isn't invited to re-peek their role.
        <div className="tap-cover" aria-live="polite">
          <div className="display">{t === 'ru' ? 'РОЛЬ ЗАПОМНЕНА' : 'РОЛЬ ЗАПАМ’ЯТАНО'}</div>
          <div className="mono">
            {t === 'ru' ? 'ЖДЁМ ОСТАЛЬНЫХ…' : 'ЧЕКАЄМО НА ІНШИХ…'}
          </div>
        </div>
      )}

      {covered && !acked && (
        <button className="tap-cover" onClick={() => setCovered(false)}>
          <div className="display">{t === 'ru' ? 'НАЖМИ' : 'НАТИСНИ'}</div>
          <div className="mono">{t === 'ru' ? 'СПРЯЧЬ ЭКРАН ОТ ДРУГИХ' : 'СХОВАЙ ЕКРАН ВІД ІНШИХ'}</div>
          <svg width="48" height="48" viewBox="0 0 24 24" style={{ marginTop: 16 }}>
            <path d="M12 4 L12 20 M5 13 L12 20 L19 13" fill="none" stroke="#ff2a3c" strokeWidth="2" />
          </svg>
        </button>
      )}
    </div>
  );
}
